import { Injectable, Injector, ComponentRef, EventEmitter, ElementRef } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import {JOYRIDE_DIALOG_DATA, JOYRIDE_DIALOG_FUNCTIONS} from './joyride.tokens';
import {JoyrideOverlayComponent} from  './joyride.component';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { Utils } from '../utils';
import * as mezr from 'mezr';
import { debounceTime } from 'rxjs/operators';

export interface JoyrideConfig {
    panelClass?: string;
    hasBackdrop?: boolean;
    backdropClass?: string;
    data?: object;
    width?: number;
    id?: string;
    order?: string[];
    scrollContainerRef?: ElementRef
}

export const DEFAULT_CONFIG: JoyrideConfig = {
    hasBackdrop: false,
    backdropClass: 'joyride-backdrop',
    panelClass: 'joyride-panel',
    width: 400
}

export interface JoyrideStep {
    name: string;
    text: string;
    title?: string;
    hasNext?: boolean;
    loading?: boolean;
    previousStep?: JoyrideStep;
    ref?: ElementRef;
    position?: string;
    first?: boolean;
    skip?: boolean;
    done?(e: JoyrideStep): void;
    next?(e: JoyrideStep): void;
}

export interface JoyrideSteps {
    [propName: string]: JoyrideStep
}

@Injectable()
export class JoyrideService {
    private _steps = new BehaviorSubject<JoyrideSteps>({});
    private steps = this._steps.asObservable();

    private _activeStep = new BehaviorSubject<JoyrideStep>(undefined);
    private activeStep = this._activeStep.asObservable();

    private _paused = new BehaviorSubject<boolean>(false);
    private paused = this._paused.asObservable();

    events = {
        next: new EventEmitter(),
        done: new EventEmitter()
    };
    end = new EventEmitter();

    private overlayRef: OverlayRef;

    isOpen:boolean = false;
    config:JoyrideConfig = {};
    
    private loadingStep: JoyrideStep = {
        name: 'loadingStep',
        text: 'Loading next step',
        hasNext: true,
        loading: true,
        previousStep: undefined,
        ref: {
            nativeElement: document.body
        },
        position: 'center'
    }

    windowResizeSubscription;
    scrollSubscription;

    constructor(private overlay: Overlay, private injector: Injector, private utils: Utils) {
        this.steps.subscribe(steps => {
            if( !steps ) {
                return;
            }
            const configOrder = this.config.order;
            let lastOrderIndex = 0;
            if( configOrder ) {
                lastOrderIndex = configOrder.length - 1;
            }
            const keys = Object.keys(steps);
            const lastIndex = keys.length - 1;
            const activeStep = this._activeStep.getValue();
            const activeStepName = activeStep ? activeStep.name : false;
            for(var i = 0; i < keys.length; i++) {
                const name = keys[i];
                const step = steps[name];
                step.hasNext = false;
                if( configOrder ) {
                    const orderIndex = configOrder.indexOf(name);
                    if( orderIndex < lastOrderIndex ) {
                        step.hasNext = true;
                    }
                } else {
                    if( i < lastIndex ) {
                        step.hasNext = true;
                    }
                }
                if( name === activeStepName ) {
                    this._activeStep.next(step);
                }
            }
        });

    }
    removeStep(data: JoyrideStep|JoyrideStep[]):void {
        const steps = this._steps.getValue();
        const items = Array.isArray(data) ? data : [data];
        for(var i = 0; i < items.length; i++) {
            const name = items[i].name;
            if( steps.hasOwnProperty(name) ) {
                delete steps[name];
            }
        }
        this._steps.next(steps);
    }
    setStep(data, ref) {
        const steps = this._steps.getValue();
        const items = Array.isArray(data) ? data : [data];
        for(var i = 0; i < items.length; i++) {
            const step = items[i];
            Object.assign(step, {
                ref: ref
            });
            steps[step.name] = step;
        }
        this._steps.next(steps);
    }
    start(config:JoyrideConfig = {}) {
        this.done();
        this.config = config;
        this.maybeOpenDialog(config);
    }
    openTimeout;
    maybeOpenDialog(config:JoyrideConfig, index = 0) {
        const steps = this._steps.getValue();
        const stepKeys = config.order ? config.order : Object.keys(steps);
        const firstStepName = stepKeys.length > 0 ? stepKeys[0] : false;
        const firstStep = firstStepName ? steps[firstStepName] : false;
        if( firstStep && index < 40 ) {
            if( config.order ) {
                const steps = this._steps.getValue();
                this._steps.next(steps);
            }
            clearTimeout(this.openTimeout);
            firstStep.first = true;
            this._activeStep.next(firstStep);
            this.open(config, firstStep);
            return;
        }
        this.openTimeout = setTimeout(() => {
            index++;
            this.maybeOpenDialog(config, index);
        }, 50);
    }
    destroy() {
        clearTimeout(this.openTimeout);
        clearTimeout(this.navigateTimeout);
        clearTimeout(this.repositionTimer);
        this._steps.next({});
        this._activeStep.next(undefined);
        if( this.overlayRef ) {
            this.overlayRef.dispose();
        }
        this.isOpen = false;
        if( this.windowResizeSubscription ) {
            this.windowResizeSubscription.unsubscribe();
        }
        if( this.scrollSubscription ) {
            this.scrollSubscription.unsubscribe();
        }
    }
    private done() {
        const step = this._activeStep.getValue();
        if( step && typeof step.done === 'function') {
            step.done(step);
        }
        this.events.done.emit(step);
        this.end.emit(step);
        this._paused.next(false);
        if( this.overlayRef ) {
            this.overlayRef.dispose();
        }
        this.isOpen = false;
        if( this.windowResizeSubscription ) {
            this.windowResizeSubscription.unsubscribe();
        }
        if( this.scrollSubscription ) {
            this.scrollSubscription.unsubscribe();
        }
    }
    private overlayInViewport(bounds) {
        return (
            bounds.top >= 0 && bounds.bottom <= window.innerHeight
        );
    }
    private maybeScrollIntoView(data) {
        const bounds = data.ref.nativeElement.getBoundingClientRect();
        if( this.overlayInViewport(bounds) ) {
            return;
        }

        const scrollEl = this.config.scrollContainerRef ? this.config.scrollContainerRef.nativeElement : document.body;
        const scrollTo = (window.innerHeight - bounds.height) / 2;
        scrollEl.scrollTop = scrollTo + scrollEl.scrollTop;
    }
    prevPosition;
    repositionTimer;
    private reposition(data: JoyrideStep, userScrolled: boolean = false) {
        clearTimeout(this.repositionTimer);
        const overlayEl = this.overlayRef.overlayElement;
        if( !overlayEl ) {
            return;
        }

        if( !userScrolled ) {
            this.maybeScrollIntoView(data);
        }

        overlayEl.classList.remove(...['arrow-right', 'arrow-left', 'arrow-top', 'arrow-bottom', 'arrow-center', 'arrow-hide']);

        if( data.loading ) {
            overlayEl.classList.add('arrow-hide');
            return;
        }

        let position = 'right center left center';
        const dataPosition = data.position;
        if( dataPosition == 'center' ) {
            position = 'center center center center';
            overlayEl.classList.add('arrow-center');
        }
        if( dataPosition == 'left' ) {
            position = 'right center left center';
            overlayEl.classList.add('arrow-right');
        }
        if( dataPosition == 'right' ) {
            position = 'left center right center';
            overlayEl.classList.add('arrow-left');
        }
        if( dataPosition == 'top' ) {
            position = 'center bottom center top';
            overlayEl.classList.add('arrow-bottom');
        }
        if( dataPosition == 'bottom' ) {
            position = 'center top center bottom';
            overlayEl.classList.add('arrow-top');
        }

        const newPosition = mezr.place({
            element: overlayEl,
            target: data.ref.nativeElement,
            position: position,
            contain: {
                within: [window, 'padding'],
                onOverflow: {
                    top: 'none',
                    bottom: 'none',
                    left: 'push',
                    right: 'push'
                }
            },
            adjust: (position, info) => {
                if( info.overflowCorrection.left !== 0 ) {
                    overlayEl.classList.add('arrow-center');
                }
            }
        });

        if( JSON.stringify(this.prevPosition) === JSON.stringify(newPosition) ) {
            return;
        }
        this.prevPosition = newPosition;

        if( data.first ) {
            overlayEl.style.top = newPosition.top + 'px';
            overlayEl.style.left = newPosition.left + 'px';
            overlayEl.style.transition = 'opacity .2s ease';
            overlayEl.style.opacity = '1';
            overlayEl.style.pointerEvents = 'auto';
        }

        const fromTop = overlayEl.offsetTop;
        const toTop = newPosition.top;
        const distanceTop = toTop - fromTop;
        const fromLeft = overlayEl.offsetLeft;
        const toLeft = newPosition.left;
        const distanceLeft = toLeft - fromLeft;

        const animateTop = fromTop !== toTop;
        const animateLeft = fromLeft !== toLeft;
        if( !animateLeft && !animateTop ) {
            return;
        }

        this.utils.animate({
            timing: 'easeInOutCubic',
            draw: (progress) => {
                if( animateTop ) {
                    overlayEl.style.top = fromTop + (distanceTop * progress) + 'px';
                }
                if( animateLeft ) {
                    overlayEl.style.left = fromLeft + (distanceLeft * progress) + 'px';
                }
            }
        });

        this.repositionTimer = setTimeout(() => {
            this.reposition(data, userScrolled);
        }, 50);
    }
    private getNextStep(currentStep) {
        const steps = this._steps.getValue();
        const order = this.config.order;
        const keys = order ? order : Object.keys(steps);
        const index = keys.indexOf(currentStep.name);
        const nextIndex = index + 1;
        const key = keys[nextIndex];
        if( !key ) {
            return undefined;
        }
        const nextStep = steps[key];
        if( !nextStep ) {
            return undefined;
        }
        if( nextStep.skip === true ) {
            return this.getNextStep(nextStep);
        }
        return nextStep;
    }
    private addLoadingStep(step) {
        this.loadingStep.previousStep = step;
        this._activeStep.next(this.loadingStep);
    }
    public pause() {
        this._paused.next(true);
    }
    public resume() {
        this._paused.next(false);
    }
    navigateTimeout;
    private navigate() {
        let step = this._activeStep.getValue();
        if( step.name == 'loadingStep' ) {
            step = step.previousStep;
        }
        const nextStep = this.getNextStep(step);
        if( !nextStep ) {
            this.addLoadingStep(step);
            this.navigateTimeout = setTimeout(() => {
                this.navigate();
            }, 50);
            return;
        }
        clearTimeout(this.navigateTimeout);
        if( typeof step.next === 'function') {
            step.next(step);
        }
        this._activeStep.next(nextStep);
        this.events.next.emit(step);
    }
    private open(config: JoyrideConfig = {}, firstStep): OverlayRef {
        this.windowResizeSubscription = fromEvent(window, 'resize').pipe(debounceTime(200)).subscribe(() => {
            const activeStep = this._activeStep.getValue();
            if( activeStep ) {
                this.reposition(activeStep);
            }
        });
        const el = this.config.scrollContainerRef ? this.config.scrollContainerRef.nativeElement : window;
        this.scrollSubscription = fromEvent(el, 'scroll').subscribe(() => {
            const activeStep = this._activeStep.getValue();
            if( activeStep ) {
                this.reposition(activeStep, true);
            }
        });

        const dialogConfig = { ...DEFAULT_CONFIG, ...config };

        const overlayRef = this.createOverlay(dialogConfig);
        overlayRef.overlayElement.style.opacity = '0';
        overlayRef.overlayElement.style.pointerEvents = 'none';
        this.overlayRef = overlayRef;
    
        this.attachDialogContainer(overlayRef, dialogConfig);

        overlayRef.backdropClick().subscribe(_ => this.done());

        this.isOpen = true;
        this.reposition(firstStep);

        return overlayRef;
    }
    private attachDialogContainer(overlayRef: OverlayRef, config: JoyrideConfig) {
        const injector = this.createInjector(config, overlayRef);
    
        const containerPortal: ComponentPortal<JoyrideOverlayComponent> = new ComponentPortal(JoyrideOverlayComponent, null, injector);
        const containerRef: ComponentRef<JoyrideOverlayComponent> = overlayRef.attach(containerPortal);
    
        return containerRef.instance;
    }
    private createInjector(config: JoyrideConfig, overlayRef: OverlayRef): PortalInjector {
        // Instantiate new WeakMap for our custom injection tokens
        const injectionTokens = new WeakMap();

        // Set custom injection tokens
        injectionTokens.set(OverlayRef, overlayRef);
        injectionTokens.set(JOYRIDE_DIALOG_DATA, this.activeStep);
        injectionTokens.set(JOYRIDE_DIALOG_FUNCTIONS, {
            next: this.navigate.bind(this),
            done: this.done.bind(this),
            paused: this.paused,
            reposition: this.reposition.bind(this)
        });

        // Instantiate new PortalInjector
        return new PortalInjector(this.injector, injectionTokens);
    }
    private createOverlay(config: JoyrideConfig): OverlayRef {
        // Returns an OverlayConfig
        const overlayConfig = this.getOverlayConfig(config);

        // Returns an OverlayRef
        return this.overlay.create(overlayConfig);
    }
    private getOverlayConfig(config: JoyrideConfig = {}): OverlayConfig {
        const overlayConfig = new OverlayConfig({
            hasBackdrop: config.hasBackdrop,
            backdropClass: config.backdropClass,
            panelClass: config.panelClass,
            width: config.width,
            scrollStrategy: this.overlay.scrollStrategies.block()
        });

        return overlayConfig;
    }
}